import React from "react";
import Box from "@mui/material/Box";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RouteIcon from "@mui/icons-material/Route";
import Tooltip from "@mui/material/Tooltip";
import {
    PlannedWorkShiftDto,
    PlannedWorkShiftSaveDto,
} from "../../models/plannedWorkShift/plannedWorkShift";
import { InlineEdit } from "./InlineEdit";
import { VehicleSelect } from "../transport/components/vehicleSelect";
import { DriverSelect } from "../transport/components/driverSelect";
import { t } from "i18next";
import { RouteSelect } from "../framework/select/routeSelect";

export interface WorkShiftFieldEditorProps {
    shift: PlannedWorkShiftDto;
    handleSave: (newValue: any, fieldName: keyof PlannedWorkShiftSaveDto) => Promise<void>;
    startOpen?: boolean;
}

interface RouteEditorProps extends WorkShiftFieldEditorProps {
    onCreateRoute?: () => void;
}

export const VehicleEditor = ({
    shift,
    handleSave,
    startOpen,
    vehicleHasConflict,
}: WorkShiftFieldEditorProps & { vehicleHasConflict?: boolean }) => {
    const hasVehicle = !!shift.vehicleId;
    const vehicleBg = vehicleHasConflict ? "error.main" : hasVehicle ? "primary.main" : "grey.400";
    const vehicleName = shift.vehicleName ?? t("common.notSelected");

    const IconBadge: React.FC<{ bg?: string; children: React.ReactNode }> = ({ bg = "grey.500", children }) => (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: 20,
                minWidth: 20,
                px: 0.5,
                borderRadius: 0.5,
                bgcolor: bg,
                lineHeight: 0,
            }}
        >
            {children}
        </Box>
    );

    const iconElement = (
        <Tooltip title={t("workShiftPlanning.icons.vehicleTooltip", { name: vehicleName })}>
            <span onClick={(e) => { e.stopPropagation(); }} style={{ cursor: "pointer" }}>
                <IconBadge bg={vehicleBg}>
                    <LocalShippingOutlinedIcon sx={{ fontSize: 16, color: "common.white" }} />
                </IconBadge>
            </span>
        </Tooltip>
    );

    return (
        <InlineEdit
            value={shift.vehicleId}
            displayValue={shift.vehicleName}
            emptyText={t("workShiftPlanning.noVehicle")}
            iconElement={iconElement}
            startEditing={!!startOpen}
            handleSave={(val) => handleSave(val, "vehicleId")}
            renderEdit={({ value, onChange, onBlur, onKeyDown }) => (
                <Box mt={1}>
                    <VehicleSelect
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        onKeyDown={onKeyDown}
                        autoFocus
                        open
                        small
                    />
                </Box>
            )}
        />
    );
};

export const EmployeeEditor = ({
    shift,
    handleSave,
    startOpen,
    employeeHasConflict,
}: WorkShiftFieldEditorProps & { employeeHasConflict?: boolean }) => {
    const hasEmployee = !!shift.employeeId;
    const employeeBg = employeeHasConflict ? "error.main" : hasEmployee ? "primary.main" : "grey.400";
    const employeeName = shift.employeeName ?? t("common.notSelected");

    const IconBadge: React.FC<{ bg?: string; children: React.ReactNode }> = ({ bg = "grey.500", children }) => (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: 20,
                minWidth: 20,
                px: 0.5,
                borderRadius: 0.5,
                bgcolor: bg,
                lineHeight: 0,
            }}
        >
            {children}
        </Box>
    );

    const iconElement = (
        <Tooltip title={t("workShiftPlanning.icons.employeeTooltip", { name: employeeName })}>
            <span onClick={(e) => { e.stopPropagation(); }} style={{ cursor: "pointer" }}>
                <IconBadge bg={employeeBg}>
                    <PersonOutlineIcon sx={{ fontSize: 16, color: "common.white" }} />
                </IconBadge>
            </span>
        </Tooltip>
    );

    return (
        <InlineEdit
            value={shift.employeeId}
            displayValue={shift.employeeName}
            emptyText={t("workShiftPlanning.noEmployee")}
            iconElement={iconElement}
            startEditing={!!startOpen}
            handleSave={(val) => handleSave(val, "employeeId")}
            renderEdit={({ value, onChange, onBlur, onKeyDown }) => (
                <Box mt={1}>
                    <DriverSelect
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        onKeyDown={onKeyDown}
                        autoFocus
                        open
                        small
                    />
                </Box>
            )}
        />
    );
};

export const RouteEditor = ({
    shift,
    handleSave,
    onCreateRoute,
    startOpen,
}: RouteEditorProps & { startOpen?: boolean }) => (
    <InlineEdit
        value={shift.routeId}
        displayValue={shift.routeName}
        emptyText={t("workShiftPlanning.noRoute")}
        iconElement={(
            <Tooltip title={t("workShiftPlanning.icons.routeTooltip", { name: shift.routeName ?? t("common.notSelected") })}>
                <span onClick={(e) => { e.stopPropagation(); }} style={{ cursor: "pointer" }}>
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: 20,
                            minWidth: 20,
                            px: 0.5,
                            borderRadius: 0.5,
                            bgcolor: !!shift.routeId ? "primary.main" : "grey.400",
                            lineHeight: 0,
                        }}
                    >
                        <RouteIcon sx={{ fontSize: 16, color: "common.white" }} />
                    </Box>
                </span>
            </Tooltip>
        )}
        startEditing={!!startOpen}
        handleSave={(val) => handleSave(val, "routeId")}
        renderEdit={({ value, onChange, onBlur, onKeyDown }) => (
            <Box mt={1}>
                <RouteSelect
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    onKeyDown={onKeyDown}
                    onCreateRoute={onCreateRoute}
                    autoFocus
                    open
                    small
                />
            </Box>
        )}
    />
);

